import { AuthenticationResult } from 'src/core/services/authentication';
import { ScopeType, ScopeTypeValue, Selector, SelectorValue } from './types';
import { TableModel } from 'src/core/ui/collections/table';
import i18n from 'src/i18n';

export function isUndefined(o: any) {
  return o === undefined;
}
export function isNullOrUndefined(o: any) {
  return o === null || o === undefined;
}

export function getCurrentOrg(identity: AuthenticationResult): { id: string; name: string } {
  return { id: identity?.profile['tenant_id'], name: identity?.profile['tenant_name'] };
}

const selectorsOrder = [Selector.Drillhole, Selector.Ring, Selector.Project, Selector.Organization, Selector.Partners];
export { selectorsOrder };

export function generateSelectorOdataFilter(minSelector: Selector, maxSelector: Selector): string {
  const start = selectorsOrder.findIndex((x) => x === minSelector);
  const end = selectorsOrder.findIndex((x) => x === maxSelector);
  if (start === -1 || end === -1 || end < start) return '';
  return (
    '(Selector eq ' +
    selectorsOrder
      .slice(start, end + 1)
      .map((x) => `'${x}'`)
      .join(' or Selector eq ') +
    ')'
  );
}

export function getRolePermissionSelectors(roleSelector: Selector): { minSelector: Selector; maxSelector: Selector } {
  return { minSelector: Selector.Drillhole, maxSelector: roleSelector };
}

export const getKeyValuePairs = <V>(o: any): { key: string; value: V }[] => {
  const result = [];
  for (const key in o) {
    if (o.hasOwnProperty(key)) {
      result.push({ key, value: (o[key] as unknown) as V });
    }
  }

  return result;
};

export const toObject = <T, V>(o: T[], keySelector: (o: T) => string, valueSelector: (o: T) => V): { [key: string]: V } => {
  return (o || []).reduce((acc: { [key: string]: V }, el: T) => {
    acc[keySelector(el)] = valueSelector(el);
    return acc;
  }, {} as { [key: string]: V }) as { [key: string]: V };
};

export const deCamelCase = (text: string) => {
  return text.replace(/([a-z](?=[A-Z]))/g, '$1 ');
};

export const emptyTableModel: TableModel<any> = {
  query: { searchQuery: '', skip: 0, take: 0 },
  data: { count: 0, items: [], isBusy: false },
  columns: [],
};

export const getSelectorFromScope = (scope: ScopeType) => {
  switch (scope) {
    case ScopeType.Organization:
      return Selector.Organization;
    case ScopeType.Project:
      return Selector.Project;
    case ScopeType.Ring:
      return Selector.Ring;
    case ScopeType.Drillhole:
    case ScopeType.Survey:
      return Selector.Drillhole;
  }
};

export const getRoleDisplayName = (roleName: string) => (i18n.t(roleName) === roleName ? roleName : `${roleName} (${i18n.t(roleName)})`);

export const getDateFormat = () => {
  const language = i18n.language;

  switch (language) {
    case 'en':
      return 'MM/DD/YYYY';
    case 'es':
      return 'DD/MM/YYYY';
    default:
      return 'DD/MM/YYYY';
  }
};

export const addPaddingToImage = async (base64: string, padding: number = 16) => {
  const img = new Image();
  img.src = base64;

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = img.width + padding * 2;
  canvas.height = img.height + padding * 2;
  ctx!.fillStyle = 'white';
  ctx!.fillRect(0, 0, canvas.width, canvas.height);
  ctx!.drawImage(img, padding, padding);
  const url = canvas.toDataURL('image/png');
  const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve));

  return { blob, url };
};

export const getScopeTypeRangeBySelector = (selector?: Selector): ScopeType[] => {
  if (selector == null) return [ScopeType.Organization, ScopeType.Project, ScopeType.Ring, ScopeType.Drillhole];
  return [ScopeType.Organization, ScopeType.Project, ScopeType.Ring, ScopeType.Drillhole].filter(
    (s) => ScopeTypeValue[s] >= Math.min(SelectorValue[selector], SelectorValue.Organization)
  );
};

export const isScopeTypeSelectorOk = (selector: Selector, type: ScopeType): boolean => {
  if (type == null || type === ScopeType.Survey) return false;
  if (selector == null) return false;
  const typeValue = ScopeTypeValue[type];
  const selectorValue = Math.min(SelectorValue[selector], SelectorValue.Organization);
  return typeValue >= selectorValue;
};
